<template>
  <Transition name="typing-fade">
    <div
      v-if="visibleLabel"
      class="chat-typing px-3 py-1.5 text-[11px] text-text-muted border-t border-white/5 flex items-center gap-2"
      aria-live="polite"
    >
      <span class="chat-typing__dots" aria-hidden="true">
        <span></span><span></span><span></span>
      </span>
      <span class="truncate">{{ visibleLabel }}</span>
    </div>
  </Transition>
</template>

<script setup>
import { computed } from 'vue'
import { useTypingIndicator } from '~/composables/chat/useTypingIndicator'

const props = defineProps({
  threadId: { type: String, required: true },
  // Map<userId, { name, avatar }> — used to render names. Falls back to
  // "Someone" when an id isn't known.
  participants: { type: Map, default: () => new Map() }
})

const { typingUserIds } = useTypingIndicator()

const visibleLabel = computed(() => {
  const ids = typingUserIds(props.threadId)
  if (!ids.length) return ''
  const names = ids.map(id => props.participants.get(id)?.name || 'Someone')
  if (names.length === 1) return `${names[0]} is typing…`
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`
  return `${names[0]}, ${names[1]} and ${names.length - 2} more are typing…`
})
</script>

<style scoped>
/* Three pulsing dots — matches the text color so it blends with the label. */
.chat-typing__dots {
  display: inline-flex;
  gap: 3px;
}
.chat-typing__dots span {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.6;
  animation: typing-bounce 1.2s infinite ease-in-out;
}
.chat-typing__dots span:nth-child(2) { animation-delay: 0.15s; }
.chat-typing__dots span:nth-child(3) { animation-delay: 0.3s; }

@keyframes typing-bounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.3; }
  40% { transform: translateY(-3px); opacity: 1; }
}

.typing-fade-enter-active,
.typing-fade-leave-active {
  transition: opacity 160ms ease, max-height 200ms ease;
  overflow: hidden;
}
.typing-fade-enter-from,
.typing-fade-leave-to {
  opacity: 0;
  max-height: 0;
}
.typing-fade-enter-to,
.typing-fade-leave-from {
  opacity: 1;
  max-height: 32px;
}
</style>
